import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDate } from '@/lib/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { terms as api } from '@/api/endpoints'
import { Alert, Checkbox, Modal, Spinner } from '@/components/ui'
import { useAuth } from '@/store/auth'

/**
 * Okno s novým znením obchodných podmienok.
 *
 * Prečo vôbec: VOP nie sú záväzné tým, že visia na webe — podľa § 273
 * Obchodného zákonníka sa stanú súčasťou zmluvy, len ak ich druhá strana
 * poznala. Kliknutie tu je ten dôkaz; zapisuje sa verzia, čas, adresa
 * a odtlačok znenia.
 *
 * Zámerne **nie je** neodbytné. Bod 8.4 VOP hovorí, že ďalšie používanie
 * systému po účinnosti novej verzie sa už samo považuje za prijatie, takže
 * okno sa dá zavrieť a pracovať ďalej. Blokovať prácu recepcie kvôli
 * právnemu dokumentu, ktorý aj tak platí, by bola šikana — a v januári
 * uprostred sezóny drahá.
 *
 * Vidí ho len správca a vlastník: podmienky prijíma škola, nie ten, kto
 * práve stojí pri pulte.
 */
export default function TermsGate() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const isAdmin = Boolean(useAuth((s) => s.user)?.can_administrate)
  const [dismissed, setDismissed] = useState(false)
  const [read, setRead] = useState(false)
  const [error, setError] = useState('')

  const { data } = useQuery({
    queryKey: ['terms'],
    queryFn: api.get,
    enabled: isAdmin,
    // Nemení sa počas dňa; zbytočné dopytovanie pri každom prepnutí okna.
    staleTime: 60 * 60 * 1000,
  })

  const accept = useMutation({
    mutationFn: api.accept,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['terms'] })
      void qc.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const open = Boolean(isAdmin && data?.needs_acceptance && !dismissed)

  return (
    <Modal
      open={open}
      onClose={() => setDismissed(true)}
      size="lg"
      title={t('terms.title', { version: data?.version ?? '' })}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDismissed(true)}
            disabled={accept.isPending}
          >
            {t('terms.later')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => accept.mutate()}
            disabled={!read || accept.isPending}
          >
            {accept.isPending ? <Spinner /> : null}
            {t('terms.accept')}
          </button>
        </div>
      }
    >
      {!data ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            {t('terms.intro', {
              date: formatDate(data.effective_from),
            })}
          </p>

          {error ? <Alert variant="error">{error}</Alert> : null}

          <div
            className="max-h-[52vh] overflow-y-auto rounded border border-slate-200 bg-slate-50/60 p-4 text-sm leading-relaxed [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-5 [&_h2]:text-sm [&_h2]:font-semibold [&_li]:mb-1 [&_ol]:ml-5 [&_ol]:list-[lower-alpha] [&_p]:mb-2 [&_table]:my-2 [&_table]:w-full [&_td]:border-b [&_td]:border-slate-200 [&_td]:py-1 [&_td]:pr-2 [&_td]:align-top [&_th]:py-1 [&_th]:pr-2 [&_th]:text-left"
            // Znenie prichádza z nášho backendu, nie od používateľa — je to
            // ten istý text, aký je na verejnej adrese VOP.
            dangerouslySetInnerHTML={{ __html: data.body }}
          />

          <Checkbox
            checked={read}
            onChange={(e) => setRead(e.target.checked)}
            label={t('terms.confirm')}
          />

          <p className="text-xs text-slate-500">
            {t('terms.note')}{' '}
            <a href={data.url} target="_blank" rel="noreferrer" className="underline">
              {data.url}
            </a>
          </p>
        </div>
      )}
    </Modal>
  )
}
